import { in_array, strval, equal, intval, strtolower, trim, empty } from '../../php-compat';
import type { PortEnv } from '../../port-contract';
import { isArray, slot, values, type Arr } from './access';
export interface FabricateGateDeps {
    item_field(row: Arr, field: string): unknown;
    layer_value(row: Arr, layer: string): unknown;
    layer_entries(row: Arr, layer: string): Arr;
    ft_fabricate_rows(layer: Arr): Arr;
    query_full_item_arr(id: string): unknown;
}
const CASTABLE = ['ability', 'full_ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability',
    'lair_ability', 'menta', 'sanguine', 'potion', 'coating', 'food', 'drink', 'elixir', 'spell', 'curse'];
const DELAYED = ['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours'];
const FINISH = ['finish_casting', 'finish_casting_ritual_spell'];
// RollType::gathering_roll; this dispatch gate needs no runtime catalog lookup.
const GATHERING_ROLL = 'FA17A3A2-3068-C84C-1EA5-C511F71FBD8B';
export const FABRICATE_WORLD_PLACEMENT_ITEM_TYPES: readonly string[] = ['furniture', 'door', 'building', 'window', 'wall', 'roof', 'floor'];
/** PHP: ft_fabricate_should_grant (includes/functions/janus/items/fabricate_on_cast.php:170) */
export function ftFabricateShouldGrant(item: Arr, phase: unknown, env: PortEnv<FabricateGateDeps>): boolean {
    if (!in_array(strval(env.deps.item_field(item, 'item_type') ?? ''), CASTABLE, true)) return false;
    if (equal(env.deps.layer_value(item, 'roll_type'), GATHERING_ROLL)) return false;
    const marker = strval(phase ?? '');
    if (in_array(marker, ['damage_only', 'end_turn_in_p_aoe', 'moving_into_p_aoe', 'read', 'finish_reading', 'unread_book', 'finish_training',
        'arm_counterspell', 'finish_gathering', 'finish_lockpicking', 'finish_crafting_wip', 'finish_disenchanting', 'throw_weapon'], true)) return false;
    const delayed = in_array(strval(env.deps.layer_value(item, 'spell_casting_time') ?? ''), DELAYED, true), finish = in_array(marker, FINISH, true);
    return delayed ? finish : !finish;
}
/** PHP: ft_fabricate_allows_targetless_cast (includes/functions/janus/items/fabricate_on_cast.php:213) */
export function ftFabricateAllowsTargetlessCast(item: Arr, phase: unknown, env: PortEnv<FabricateGateDeps>): boolean {
    const marker = strval(phase ?? ''), time = strval(env.deps.layer_value(item, 'spell_casting_time') ?? ''),
        delayedStart = in_array(time, DELAYED, true) && in_array(marker, ['', 'spell_ritual'], true);
    if (!ftFabricateShouldGrant(item, phase, env)) {
        const castable = in_array(strval(env.deps.item_field(item, 'item_type') ?? ''), CASTABLE, true),
            gathering = equal(env.deps.layer_value(item, 'roll_type'), GATHERING_ROLL);
        if (!delayedStart || !castable || gathering) return false;
    }
    for (const row of values(env.deps.ft_fabricate_rows(env.deps.layer_entries(item, 'fabricate')))) {
        const output = env.deps.query_full_item_arr(slot(row, 'item_uuid') as string);
        if (!isArray(output) || empty(output)) continue;
        if (!in_array(strval(env.deps.item_field(output, 'item_type') ?? ''), FABRICATE_WORLD_PLACEMENT_ITEM_TYPES, true)) return true;
    }
    return false;
}
/** PHP: ft_fabricate_expiry_override (includes/functions/janus/items/fabricate_on_cast.php:270) */
export function ftFabricateExpiryOverride(item: Arr, env: PortEnv<FabricateGateDeps>): Arr | null {
    if (intval(env.deps.layer_value(item, 'fabricate_expires_after_duration') ?? 0) === 0) return null;
    const duration = env.deps.layer_value(item, 'duration_int'), type = strtolower(trim(strval(env.deps.layer_value(item, 'duration_type') ?? '')));
    if (duration === null || intval(duration) < 1 || !in_array(type, ['seconds', 'rounds', 'minutes', 'hours', 'days', 'weeks', 'months', 'years'], true)) return null;
    return { int: intval(duration), type };
}
